import { type TFunction } from 'i18next'

import { type SegmentDisplayType } from '~/utils'

import { type NpiDriverAnswerData } from '../../hooks/useNpiDriverTableData'

// スコア上位3件の option を返す（0以下は対象外、同点は option 昇順）。
// NpiDriverTable の推奨WHAT軸判定（recommendedWhatAxisOptions）と同じロジックで、
// テーブルの✓とラベルの母集団・順位付けを一致させる。
const topThreeOptions = (
  scores: { option: number; score: number }[],
): number[] =>
  scores
    .filter((s) => s.score > 0)
    .sort((a, b) => b.score - a.score || a.option - b.option)
    .slice(0, 3)
    .map((s) => s.option)

// AIコメント生成に添える補足ブロックを組み立てる。表には列として出さず（画面の見た目は
// 変えない）、tabulationData の末尾にだけ足すためのテキスト。含める情報は:
//   - Index（重視点率[from] × NPIドライバー差分[to-from]）上位3件＝訴求優先度が高い選択肢
//   - PoD スコア上位3件＝差別化しやすい選択肢（自社の強み・推奨WHAT軸の根拠）
//
// PoD/Index の生スコアは内部指標で顧客向けコメントに出すと誤読・用語漏れの原因になるため、
// テーブルの✓と同じ top3 ロジックで「どの選択肢が上位か」だけをラベルとして渡す。比較優位
// ブランド数は既存の表テキスト（comparisonAdvantage 列）に含まれるためここでは扱わない。
// 表示行が無い（表が非表示）ときは空文字を返し、呼び出し側で追記をスキップさせる。
export const buildNpiDriverAiSupplement = ({
  fromSegmentNumber,
  rows,
  segmentDisplayType,
  t,
  toSegmentNumber,
}: {
  fromSegmentNumber: number
  rows: NpiDriverAnswerData[]
  segmentDisplayType: SegmentDisplayType
  t: TFunction
  toSegmentNumber: number
}): string => {
  if (rows.length === 0) {
    return ''
  }

  const rowsWithScore = rows.map((row) => {
    const npiRatioBySegment =
      segmentDisplayType === 'fiveSegs'
        ? row.npiRatioByFiveSegment
        : row.npiRatioByNineSegment
    const importanceRatioBySegment =
      segmentDisplayType === 'fiveSegs'
        ? row.importanceRatioByFiveSegment
        : row.importanceRatioByNineSegment
    // Index の定義は NpiDriverTable の dataWithRaw と同じ。
    const npiDiff =
      (npiRatioBySegment[toSegmentNumber] ?? 0) -
      (npiRatioBySegment[fromSegmentNumber] ?? 0)
    const importanceRatio = importanceRatioBySegment[fromSegmentNumber] ?? 0

    return {
      index: importanceRatio * npiDiff,
      option: row.option,
      podScore: row.podScore ?? 0,
      text: row.text,
    }
  })

  const optionLabel = (option: number): string => {
    const row = rowsWithScore.find((r) => r.option === option)

    return row === undefined ? `No.${option}` : `No.${row.option} ${row.text}`
  }

  const priorityOptions = topThreeOptions(
    rowsWithScore.map((r) => ({ option: r.option, score: r.index })),
  )
  const differentiationOptions = topThreeOptions(
    rowsWithScore.map((r) => ({ option: r.option, score: r.podScore })),
  )

  const contentLines: string[] = []

  if (priorityOptions.length > 0) {
    contentLines.push(
      t('npi_driver_ai_note_priority', {
        options: priorityOptions.map(optionLabel).join('、'),
      }),
    )
  }
  if (differentiationOptions.length > 0) {
    contentLines.push(
      t('npi_driver_ai_note_differentiation', {
        options: differentiationOptions.map(optionLabel).join('、'),
      }),
    )
  }

  // 出す中身が一つも無ければ見出しだけの空ブロックにせず、空文字を返す。
  if (contentLines.length === 0) {
    return ''
  }

  return [t('npi_driver_ai_note_heading'), ...contentLines].join('\n')
}
